import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Box,
	Typography,
	Button,
	TextField,
	Grid,
	Checkbox,
	FormControlLabel,
	Paper,
	Divider,
	IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import { UsuarioDistribuidorService } from "../../service/usuario_distribuidor_service";
import { UnidadeDistribuidorService } from "../../service/unidade_distribuidor_service";
import { useUser } from "../../context/UserContext";

const IDADE_MINIMA = 18;

/** Data (aaaa-mm-dd) de quem completa a idade mínima hoje: o limite do campo. */
const dataLimiteNascimento = () => {
	const hoje = new Date();
	const limite = new Date(hoje.getFullYear() - IDADE_MINIMA, hoje.getMonth(), hoje.getDate());
	const m = String(limite.getMonth() + 1).padStart(2, "0");
	const d = String(limite.getDate()).padStart(2, "0");
	return `${limite.getFullYear()}-${m}-${d}`;
};

function UsuariosCad() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [usuario, setUsuario] = useState({
		nome: "",
		email: "",
		telefone: "",
		dataNascimento: "",
	});
	const [unidadesDistribuidoras, setUnidadesDistribuidoras] = useState([]);
	const [selectAll, setSelectAll] = useState(false);

	// empresaDistribuidorId vem do perfil em memória (UserContext),
	// com fallback no id da empresa selecionada (localStorage).
	const { user } = useUser();
	const empresaDistribuidorId = user?.empresaDistribuidorId || localStorage.getItem("selected_empresa_id");

	useEffect(() => {
		initData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const initData = async () => {
		setLoading(true);
		try {
			if (id) {
				await loadUsuario(id);
			} else {
				await loadUnidadesForEmpresa();
			}
		} catch (error) {
			console.error("Erro ao carregar dados:", error);
			alert(error.message || "Erro ao carregar dados");
		} finally {
			setLoading(false);
		}
	};

	const loadUsuario = async (usuarioId) => {
		const usuarioService = new UsuarioDistribuidorService();
		const data = await usuarioService.getById(usuarioId);

		setUsuario({
			id: data.id,
			nome: data.nome || "",
			email: data.email || "",
			telefone: data.telefone ? data.telefone.replace("+55", "") : "",
			dataNascimento: data.dataNascimento ? data.dataNascimento.split("T")[0] : "",
		});

		await loadUnidadesForUser(data);
	};

	const loadUnidadesForUser = async (userData) => {
		const unidadeService = new UnidadeDistribuidorService();
		const empresaUnidades = await unidadeService.getByEmpresaDistribuidor(
			userData.empresaDistribuidorId || empresaDistribuidorId
		);

		const unidades = empresaUnidades.map((unidade, index) => {
			const userUnidadeIndex = userData.unidadesDistribuidoras?.findIndex(
				(u) => u.unidadeDistribuidoraId === unidade.id
			);
			return {
				id: index,
				unidadeId: unidade.id,
				nome: unidade.nome,
				selected: userUnidadeIndex >= 0,
				changed: false,
			};
		});

		setUnidadesDistribuidoras(unidades);
	};

	const loadUnidadesForEmpresa = async () => {
		if (!empresaDistribuidorId) {
			return;
		}

		const unidadeService = new UnidadeDistribuidorService();
		const empresaUnidades = await unidadeService.getByEmpresaDistribuidor(
			empresaDistribuidorId
		);

		// Obter a unidade atual selecionada
		const unidadeAtualId = localStorage.getItem("selected_unidade_id");

		const unidades = empresaUnidades.map((unidade, index) => ({
			id: index,
			unidadeId: unidade.id,
			nome: unidade.nome,
			selected: unidade.id === unidadeAtualId, // Marcar a unidade atual como selecionada
			changed: false,
		}));

		setUnidadesDistribuidoras(unidades);

		// Atualizar selectAll se todas estiverem selecionadas
		if (unidades.length > 0) {
			const allSelected = unidades.every((u) => u.selected);
			setSelectAll(allSelected);
		}
	};

	const handleChange = (field, value) => {
		setUsuario((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const formatTelefone = (value) => {
		const cleaned = value.replace(/\D/g, "");
		if (cleaned.length <= 11) {
			return cleaned
				.replace(/(\d{2})(\d)/, "($1)$2")
				.replace(/(\d{5})(\d)/, "$1-$2");
		}
		return value;
	};

	const handleTelefoneChange = (value) => {
		const formatted = formatTelefone(value);
		handleChange("telefone", formatted);
	};

	const handleSelectAllChange = (event) => {
		const checked = event.target.checked;
		setSelectAll(checked);
		const newUnidades = unidadesDistribuidoras.map((unidade) => ({
			...unidade,
			selected: checked,
			changed: true,
		}));
		setUnidadesDistribuidoras(newUnidades);
	};

	const handleUnidadeChange = (index) => {
		const newUnidades = [...unidadesDistribuidoras];
		newUnidades[index] = {
			...newUnidades[index],
			selected: !newUnidades[index].selected,
			changed: true,
		};
		setUnidadesDistribuidoras(newUnidades);

		// Update selectAll state
		const allSelected = newUnidades.every((u) => u.selected);
		setSelectAll(allSelected);
	};

	const validationSave = () => {
		if (!usuario.nome || usuario.nome.trim() === "") {
			alert("O campo Nome é obrigatório");
			return false;
		}

		// Obrigatória no cadastro; na edição, usuário antigo pode seguir sem ela.
		// aaaa-mm-dd compara certo como texto.
		if (!id && !usuario.dataNascimento) {
			alert("O campo Data de Nascimento é obrigatório");
			return false;
		}
		if (usuario.dataNascimento && usuario.dataNascimento > dataLimiteNascimento()) {
			alert(`O usuário precisa ter pelo menos ${IDADE_MINIMA} anos`);
			return false;
		}

		const hasUnidadeSelected = unidadesDistribuidoras.some((u) => u.selected);
		if (!hasUnidadeSelected && unidadesDistribuidoras.length > 0) {
			alert("Selecione pelo menos uma Unidade Distribuidora");
			return false;
		}

		// Email e telefone obrigatórios apenas na criação
		if (!id) {
			if (!usuario.email || usuario.email.trim() === "") {
				alert("O campo Email é obrigatório");
				return false;
			}

			const cleanTelefone = usuario.telefone?.replace(/\D/g, "") || "";
			if (!cleanTelefone || cleanTelefone.length < 11) {
				alert("O campo Telefone é obrigatório e deve ter 11 dígitos");
				return false;
			}
		} else {
			// Na edição, se telefone foi preenchido, validar
			const cleanTelefone = usuario.telefone?.replace(/\D/g, "") || "";
			if (cleanTelefone && cleanTelefone.length < 11) {
				alert("O Telefone deve ter 11 dígitos ou estar vazio");
				return false;
			}
		}

		return true;
	};

	const handleSalvar = async () => {
		if (!validationSave()) {
			return;
		}

		setSalvando(true);
		try {
			// Obter TODAS as unidades selecionadas, não apenas as que mudaram
			const includeUnidadesDistribuidoras = unidadesDistribuidoras
				.filter((u) => u.selected)
				.map((u) => u.unidadeId);

			const cleanTelefone = usuario.telefone?.replace(/\D/g, "") || "";

			const usuarioData = {
				id: usuario.id,
				nome: usuario.nome,
				email: usuario.email || null,
				telefone: cleanTelefone ? `+55${cleanTelefone}` : null,
				dataNascimento: usuario.dataNascimento || undefined,
				operador: false,
				admin: false,
				ativo: true,
				empresaDistribuidorId: empresaDistribuidorId,
				includeUnidadesDistribuidoras: includeUnidadesDistribuidoras,
			};

			const usuarioService = new UsuarioDistribuidorService();

			if (id) {
				await usuarioService.update(usuarioData);
				alert("Usuário atualizado com sucesso!");
			} else {
				await usuarioService.save(usuarioData);
				alert("Usuário cadastrado com sucesso!");
			}

			navigate("/usuarios");
		} catch (error) {
			console.error("Erro ao salvar usuário:", error);
			alert(error.message || "Erro ao salvar usuário");
		} finally {
			setSalvando(false);
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="usuarios">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<IconButton onClick={() => navigate("/usuarios")} sx={{ color: "#666" }}>
								<ArrowBackIcon />
							</IconButton>
							<Box>
								<Typography
									variant="h5"
									sx={{
										color: "#333",
										fontWeight: 600,
										fontSize: "22px",
										mb: 0.5,
									}}
								>
									{id ? "Editar Usuário" : "Novo Usuário"}
								</Typography>
							<Typography sx={{ color: "#777", fontSize: "14px" }}>
								{id
									? "Atualize as informações do usuário abaixo"
									: "Preencha os dados para cadastrar um novo usuário"}
							</Typography>
						</Box>
						</Box>
					</Box>

					{/* Container com fundo branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "8px",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							overflow: "hidden",
						}}
					>
						{/* Dados do Usuário */}
						<Box sx={{ p: 4, pb: 3 }}>
							<Typography
								variant="subtitle1"
								sx={{
									color: "#1a9e8f",
									fontWeight: 600,
									mb: 3,
									fontSize: "17px",
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								Dados do Usuário
							</Typography>

							<Grid container spacing={3}>
								<Grid size={{ xs: 12, md: 3 }}>
									<TextField
										fullWidth
										label="Nome Completo *"
										value={usuario.nome}
										onChange={(e) => handleChange("nome", e.target.value)}
										placeholder="Digite o nome completo do usuário"
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
										sx={{
											"& .MuiOutlinedInput-root": {
												"& fieldset": { borderColor: "#e0e0e0" },
												"&:hover fieldset": { borderColor: "#1a9e8f" },
												"&.Mui-focused fieldset": { borderColor: "#1a9e8f" },
											},
										}}
									/>
								</Grid>

								<Grid size={{ xs: 12, md: 3 }}>
									<TextField
										fullWidth
										label={id ? "Email" : "Email *"}
										type="email"
										value={usuario.email}
										onChange={(e) => handleChange("email", e.target.value)}
										placeholder="exemplo@email.com"
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
										helperText={id ? "Opcional na edição" : ""}
										sx={{
											"& .MuiOutlinedInput-root": {
												"& fieldset": { borderColor: "#e0e0e0" },
												"&:hover fieldset": { borderColor: "#1a9e8f" },
												"&.Mui-focused fieldset": { borderColor: "#1a9e8f" },
											},
										}}
									/>
								</Grid>

								<Grid size={{ xs: 12, md: 3 }}>
									<TextField
										fullWidth
										label={id ? "Telefone" : "Telefone *"}
										value={usuario.telefone}
										onChange={(e) => handleTelefoneChange(e.target.value)}
										placeholder="(00) 00000-0000"
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" }, maxLength: 14 }}
										helperText={id ? "Opcional na edição" : ""}
										sx={{
											"& .MuiOutlinedInput-root": {
												"& fieldset": { borderColor: "#e0e0e0" },
												"&:hover fieldset": { borderColor: "#1a9e8f" },
												"&.Mui-focused fieldset": { borderColor: "#1a9e8f" },
											},
										}}
									/>
								</Grid>

								<Grid size={{ xs: 12, md: 3 }}>
									<TextField
										fullWidth
										type="date"
										label={id ? "Data de Nascimento" : "Data de Nascimento *"}
										value={usuario.dataNascimento}
										onChange={(e) => handleChange("dataNascimento", e.target.value)}
										InputLabelProps={{ shrink: true, style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" }, max: dataLimiteNascimento(), min: "1900-01-01" }}
										helperText={`Somente maiores de ${IDADE_MINIMA} anos`}
										sx={{
											"& .MuiOutlinedInput-root": {
												"& fieldset": { borderColor: "#e0e0e0" },
												"&:hover fieldset": { borderColor: "#1a9e8f" },
												"&.Mui-focused fieldset": { borderColor: "#1a9e8f" },
											},
										}}
									/>
								</Grid>
							</Grid>
						</Box>

						<Divider />

						{/* Unidades Distribuidoras */}
						<Box sx={{ p: 4, pt: 3 }}>
							<Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Typography
									variant="subtitle1"
									sx={{
										color: "#1a9e8f",
										fontWeight: 600,
										fontSize: "17px",
									}}
								>
									Unidades Distribuidoras
								</Typography>
								<Typography sx={{ fontSize: "13px", color: "#999" }}>
									{unidadesDistribuidoras.filter((u) => u.selected).length} de{" "}
									{unidadesDistribuidoras.length} selecionada(s)
								</Typography>
							</Box>

							<Paper
								sx={{
									p: 3,
									bgcolor: "#fafafa",
									border: "1px solid #e8e8e8",
									boxShadow: "none",
									borderRadius: "6px",
								}}
							>
								{unidadesDistribuidoras.length > 0 ? (
									<>
										<Box
											sx={{
												mb: 3,
												pb: 2,
												borderBottom: "1px solid #e0e0e0",
											}}
										>
											<FormControlLabel
												control={
													<Checkbox
														checked={selectAll}
														onChange={handleSelectAllChange}
														sx={{
															color: "#1a9e8f",
															"&.Mui-checked": {
																color: "#1a9e8f",
															},
														}}
													/>
												}
												label={
													<Typography sx={{ fontWeight: 600, fontSize: "15px", color: "#333" }}>
														Selecionar Todas as Unidades
													</Typography>
												}
											/>
										</Box>

										<Grid container spacing={2}>
											{unidadesDistribuidoras.map((unidade, index) => (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={unidade.id}>
													<Box
														sx={{
															p: 1.5,
															bgcolor: "white",
															border: unidade.selected ? "2px solid #1a9e8f" : "1px solid #e0e0e0",
															borderRadius: "6px",
															transition: "all 0.2s",
															"&:hover": {
																borderColor: "#1a9e8f",
																boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
															},
														}}
													>
														<FormControlLabel
															control={
																<Checkbox
																	checked={unidade.selected}
																	onChange={() => handleUnidadeChange(index)}
																	sx={{
																		color: "#1a9e8f",
																		"&.Mui-checked": {
																			color: "#1a9e8f",
																		},
																	}}
																/>
															}
															label={
																<Typography
																	sx={{
																		fontSize: "14px",
																		fontWeight: unidade.selected ? 600 : 400,
																		color: unidade.selected ? "#1a9e8f" : "#555",
																	}}
																>
																	{unidade.nome}
																</Typography>
															}
															sx={{ m: 0, width: "100%" }}
														/>
													</Box>
												</Grid>
											))}
										</Grid>
									</>
								) : (
									<Box sx={{ textAlign: "center", py: 4 }}>
										<Typography
											sx={{
												color: "#999",
												fontSize: "15px",
												mb: 1,
											}}
										>
											Nenhuma unidade distribuidora encontrada
										</Typography>
										<Typography sx={{ color: "#bbb", fontSize: "13px" }}>
											Cadastre uma unidade distribuidora antes de criar usuários
										</Typography>
									</Box>
								)}
							</Paper>
						</Box>

						<Divider />

						{/* Botões de Ação */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 4,
								py: 3,
								bgcolor: "#fafafa",
							}}
						>
							<Typography sx={{ fontSize: "13px", color: "#999" }}>
								* Campos obrigatórios
							</Typography>
							<Box sx={{ display: "flex", gap: 2 }}>
								<Button
									variant="outlined"
									onClick={() => navigate("/usuarios")}
									sx={{
										color: "#666",
										borderColor: "#ddd",
										textTransform: "none",
										px: 3,
										py: 1.2,
										fontSize: "15px",
										"&:hover": {
											borderColor: "#999",
											bgcolor: "rgba(0,0,0,0.02)",
										},
									}}
								>
									Cancelar
								</Button>
								<Button
									variant="contained"
									onClick={handleSalvar}
									disabled={salvando}
									sx={{
										bgcolor: "#1a9e8f",
										"&:hover": {
											bgcolor: "#138a7d",
											boxShadow: "0 4px 12px rgba(23, 162, 184, 0.3)",
										},
										"&:disabled": {
											bgcolor: "#ccc",
										},
										textTransform: "none",
										px: 4,
										py: 1.2,
										fontSize: "15px",
										fontWeight: 500,
										borderRadius: "4px",
										boxShadow: "none",
									}}
								>
									{salvando ? "Salvando..." : id ? "Atualizar Usuário" : "Cadastrar Usuário"}
								</Button>
							</Box>
						</Box>
					</Box>
			</Box>
		</AppLayout>
	);
}

export default UsuariosCad;
